import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@shared/services/api/apiRequest";
import { Form } from "@shared/features/form/Form";
import { useForm } from "@shared/features/form/useForm";
import { ActionButtonsGroup } from "@shared/ui/ActionButtonsGroup";
import { baseInviteInstitutionSchema } from "@shared/schemas/sharedSchemas";
import { makeFormFields } from "@shared/utils/makeFormFields";
import { PanelTitle } from "@shared/ui/PanelTitle";

export function OnboardingInviteInstitution() {
  const navigate = useNavigate();

  const schema = baseInviteInstitutionSchema;

  const fields = makeFormFields([
    "firstName",
    "lastName",
    "email",
    "confirmEmail",
    "organization",
  ]);

  const submitInvitation = useMutation({
    mutationFn: async (data: z.infer<typeof schema>) => {
      return await apiRequest("/api/send-organization-invitation/", "POST", {
        email: data.email,
        name: `${data.first_name}`,
        organization: data.organization,
      });
    },
    onError: (error: Error) => {
      throw error;
    },
    onSuccess: () => {
      navigate("/");
    },
  });

  const handleCancel = () => {
    navigate("/");
  };

  const { form, handleSubmit } = useForm({
    schema,
    onSubmit: submitInvitation.mutateAsync,
  });

  const { isSubmitting, isLoading, isValid } = form.formState;

  return (
    <>
      <PanelTitle
        title="Invite an Institution"
        description="Fill out this form and we'll send you information that you can pass along to your programs."
      />
      <div className="flex-1 flex flex-col justify-between bg-brand-50 text-brand-800">
        <Form
          fields={fields}
          form={form}
          className="px-8 py-8 flex-1"
          innerClassName="justify-center"
          errorContainerClassName="px-8"
        />
        <ActionButtonsGroup
          onSecondaryClick={handleCancel}
          onPrimaryClick={handleSubmit}
          secondaryButtonText={"Cancel"}
          primaryButtonText={"Send"}
          className="px-8 py-4 border-t border-brand-800"
          isButtonsFullWidth
          primaryButtonProps={{
            disabled: !isValid || isSubmitting || isLoading,
          }}
        />
      </div>
    </>
  );
}

export default OnboardingInviteInstitution;
